import { Actions } from '../../../../../../../store/state'

/**
 * Enum for context menu item types.
 * @readonly
 * @enum {string}
 */
export const ContextMenuItemEnum = {
  REMOVE: 'remove',
  COPY: 'copy',
  CUT: 'cut',
  PASTE: 'paste',
  OPEN_PROPERTIES_PANEL: 'open_properties_panel'
}

/**
 * Enum for context menu trigger element types.
 * @readonly
 * @enum {string}
 */
export const ContextMenuElementType = {
  BLANK: 'blank',
  NODE: 'node',
  LINK: 'link'
}

/**
 * @typedef {Object} ContextMenuItem
 * @property {ContextMenuItemEnum} id
 * @property {string} label
 * @property {boolean} disabled
 * @property {UnknownAction} action
 */

/**
 * @typedef {Object} ContextMenuConfig
 * @extends ContextMenuState
 * @property {ContextMenuItem[]} items
 */

/**
 * Gets the context menu configuration based on the current selection and context.
 * @param {ContextMenuState} contextMenu - The current context menu state.
 * @param {SelectionState} selection - The current selection state.
 * @param {boolean} canPaste - Indicates if the paste action is available.
 * @returns {ContextMenuConfig} The updated context menu state with the appropriate items.
 */
export const getContextMenuConfig = (contextMenu, selection, canPaste) => {
  const selectedCells = [...selection.nodes, ...selection.links]

  if (selectedCells.length > 1) {
    return {
      ...contextMenu,
      items: [
        {
          id: ContextMenuItemEnum.REMOVE,
          label: 'Remove',
          disabled: false,
          action: Actions.removeSelection
        },
        {
          id: ContextMenuItemEnum.CUT,
          label: 'Cut',
          disabled: false,
          action: Actions.cutSelection
        },
        {
          id: ContextMenuItemEnum.COPY,
          label: 'Copy to clipboard',
          disabled: false,
          action: Actions.copySelection
        }
      ]
    }
  }

  switch (contextMenu.elementType) {
    case ContextMenuElementType.BLANK:
      return {
        ...contextMenu,
        items: [
          {
            id: ContextMenuItemEnum.PASTE,
            label: 'Paste',
            disabled: !canPaste,
            action: Actions.pasteSelection
          }
        ]
      }
    case ContextMenuElementType.NODE:
      return {
        ...contextMenu,
        items: [
          {
            id: ContextMenuItemEnum.COPY,
            label: 'Copy to clipboard',
            disabled: false,
            action: Actions.copySelection
          },
          {
            id: ContextMenuItemEnum.OPEN_PROPERTIES_PANEL,
            label: 'Edit properties',
            disabled: false,
            action: Actions.propertiesPanelOpen
          }
        ]
      }
    case ContextMenuElementType.LINK:
      return {
        ...contextMenu,
        items: [
          {
            id: ContextMenuItemEnum.REMOVE,
            label: 'Remove',
            disabled: false,
            action: Actions.removeSelection
          }
        ]
      }
    default:
      return { ...contextMenu, items: [] }
  }
}
